'use client';

import React, {createContext, ReactNode, useContext, useMemo} from 'react';

import {TrackableDiscoveryItem} from '@udemy-v2/react-browse-event-tracking';
import {BrowseCourse} from '@udemy-v2/browse-course';
import {
    isBrowseCourse,
    useFetchBundleFromCourse,
    type CertificationBundle,
    ProductCardCertificationVoucher,
} from '@udemy-v2/react-certification-preparation';
import {generateTrackingId} from '@udemy-v2/event-tracking';
import {useAvailableExamAndVoucherForTopics} from '@udemy-v2/react-certification-vouchers';

import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';

export interface VoucherContext {
    // Whether or not we are still loading voucher-related data.
    isLoading: boolean;
    // The certification voucher we want to display for the course.
    certificationVoucher: TrackableDiscoveryItem<ProductCardCertificationVoucher> | null;
    certificationTopicId: string | null;
    // A voucher-related bundle of courses, if any.
    bundledCertificationCourses: TrackableDiscoveryItem<BrowseCourse>[];
    // All courses in the certification bundle (including the current course), ordered
    // as [main course, practice exam course]. Used by CertificationVoucherBundleUnit.
    // Bundle `contents` is a `Buyable[]` union: full BrowseCourse cards in production,
    // CourseBuyable stubs in some hardcoded fallback paths.
    hydratedCertificationBundle: CertificationBundle | null;
}

export const ReactCLPVoucherContext = createContext<VoucherContext | undefined>(undefined);

export interface CLPVoucherContextProviderProps {
    children: ReactNode;
}

export const CLPVoucherContextProvider: React.FC<CLPVoucherContextProviderProps> = (props) => {
    const course = useClpCourse();

    // Build ordered list of topic IDs: primary topic first, then related topics as fallbacks.
    // This handles courses where the primary topic is an aggregate (e.g. "Amazon AWS") with no
    // exam, while the specific cert topic (e.g. "AWS Certified Solutions Architect") lives in
    // the related topics array.
    // Voucher selection still requires topic ids (cert/voucher relationships are topic-keyed),
    // so this list is kept for `useAvailableExamAndVoucherForTopics` only.
    const voucherTopicIds: string[] = [];
    if (course?.primaryTopic?.id != null) voucherTopicIds.push(String(course.primaryTopic.id));
    course?.topics?.forEach((topic) => {
        const id = String(topic.id);
        if (!voucherTopicIds.includes(id)) voucherTopicIds.push(id);
    });
    const {
        isLoading: isLoadingVouchers,
        exam,
        selectedVoucherPool,
    } = useAvailableExamAndVoucherForTopics(voucherTopicIds);

    // Fetch the certification bundle that contains the current course directly. This replaces
    // the previous hardcoded `getBundleFromCourse` lookup + manual `fetchCourseDataByIds`
    // hydration. Keyed on courseId at the API layer, so we no longer need to derive a topic id
    // (which avoids edge cases where the cert topic isn't the course's primary topic).
    const {bundle, isLoading: loadingBundle} = useFetchBundleFromCourse(course.id, {
        enabled: !!course.id,
    });

    const hydratedCertificationBundle: CertificationBundle | null = bundle ?? null;

    // The "other" courses in the bundle (e.g. paired practice test if you're on the main course).
    // `bundle.contents` is a Buyable[] union, but downstream consumers expect full
    // BrowseCourse data, so narrow with `isBrowseCourse` before exposing.
    // Memoized so the trackingId stays stable across renders.
    const bundledCertificationCourses: TrackableDiscoveryItem<BrowseCourse>[] = useMemo(() => {
        if (!hydratedCertificationBundle) return [];
        return hydratedCertificationBundle.contents
            .filter(isBrowseCourse)
            .filter((c) => c.isPracticeTestCourse !== course.isPracticeTestCourse)
            .map((c) => ({...c, frontendTrackingId: generateTrackingId()}));
    }, [hydratedCertificationBundle, course.isPracticeTestCourse]);

    return (
        <ReactCLPVoucherContext.Provider
            value={{
                isLoading: isLoadingVouchers || loadingBundle,
                certificationTopicId: exam?.topicId ?? null,
                certificationVoucher: selectedVoucherPool,
                bundledCertificationCourses,
                hydratedCertificationBundle,
            }}
        >
            {props.children}
        </ReactCLPVoucherContext.Provider>
    );
};

/**
 * Custom hook to access the CLP purchase context
 * @returns CLPVoucherContext
 * @throws Error if used outside of CLPVoucherContextProvider
 */
export function useClpVoucherContext(): VoucherContext {
    const context = useContext(ReactCLPVoucherContext);

    if (context === undefined) {
        throw new Error('useClpVoucherContext must be used within a CLPVoucherContextProvider');
    }

    return context;
}
